/**
 * /earthworks/jobs/:id — one job (GET / PATCH / DELETE /earthworks/jobs/:id).
 *
 * A full page with tabs (`?tab=`, so Back and a shared link keep the place):
 * Overview (details, status actions, edit, delete), Crew, Equipment, and the
 * EW-4 field tabs — Daily logs, Rent-ins, Permits, Hazmat — under the job's
 * warnings banner (amber; work is never blocked), then Tickets, Change
 * orders and Invoices (EW-6: the holdback panel and the job's invoices), and
 * Employee hours (office roles: clock-in vs daily-log hours by day). Forms
 * open inline, never as a modal; a status move, a delete and a removal ask
 * in-app first.
 *
 * Writes are dispatch roles. The status buttons are the moves
 * EW_JOB_TRANSITIONS allows from where the job is — a mirror for which buttons
 * to draw; the API decides. Cancelling asks for a reason (the API requires
 * one). Delete is offered on a draft or scheduled job only.
 *
 * The holdback is labelled from meta.holdbackLabel (or the job's own copy),
 * never a literal.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, Pencil, Trash2, Truck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useDeleteEwJob,
  useEwCustomers,
  useEwEquipmentOptions,
  useEwJob,
  useEwMeta,
  useEwUsers,
  useUpdateEwJob,
} from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ewRateUnitWords } from '../../lib/ew-quotes';
import {
  canDispatchEw,
  catalogLabel,
  ewDay,
  ewJobClosed,
  ewJobDeletable,
  ewJobStatusSkin,
  ewNextStatuses,
  ewRefusal,
  fmtMoney,
  holdbackLabel,
} from '../../lib/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { canAdminEw, canReadEwRates } from '../../lib/earthworks';
import { TruckBillingPanel } from './TruckBillingPanel';
import { EwJobForm } from './JobForm';
import { jobHoldback } from './JobsScreen';
import { CrewSection, EquipmentSection } from './JobSections';
import { DailyLogsSection, EwJobWarningsBanner, HazmatSection, PermitsSection, RentInsSection } from './JobFieldSections';
import { JobChangeOrdersSection, JobTicketsSection } from './JobBillingSections';
import { EwJobInvoicesSection } from './invoices/EwJobInvoices';
import { EwQvaJobTab } from './qva/EwQvaJobTab';
import { EwJobEmployeeHoursSection } from './EwEmployeeHours';
import {
  isNotFound,
  EW_EYEBROW,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwLoading,
  EwPanel,
  EwSpec,
} from './EwBits';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'crew', label: 'Crew' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'logs', label: 'Daily logs' },
  { key: 'rent-ins', label: 'Rent-ins' },
  { key: 'permits', label: 'Permits' },
  { key: 'hazmat', label: 'Hazmat' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'change-orders', label: 'Change orders' },
  { key: 'invoices', label: 'Invoices' },
  // EW-8: shown on a completed job only, to the roles that read prices.
  { key: 'quoted-vs-actual', label: 'Quoted vs actual' },
  { key: 'hours', label: 'Employee hours' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const MOVE_LABEL: Record<string, string> = {
  draft: 'Back to draft',
  scheduled: 'Schedule',
  in_progress: 'Start work',
  completed: 'Complete',
  cancelled: 'Cancel job',
};
const MOVE_BODY: Record<string, string> = {
  draft: 'Moves the job back to draft.',
  scheduled: 'Schedules the job for the crew.',
  in_progress: 'Marks the job in progress.',
  completed: 'Completes the job. A completed job takes no more changes.',
  cancelled: 'Cancels the job. A cancelled job takes no more changes.',
};

export default function EwJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/earthworks/jobs');
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const requestedTab: TabKey = TABS.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'overview';
  const pickTab = (key: TabKey) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'overview') p.delete('tab');
    else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const { user } = useAuth();
  const canDispatch = canDispatchEw(user?.role);
  const query = useEwJob(id);
  const catalog = useEwMeta().data?.data ?? null;
  const customers = useEwCustomers(canDispatch);
  const users = useEwUsers(canDispatch);
  const equipment = useEwEquipmentOptions(canDispatch);
  const update = useUpdateEwJob();
  const del = useDeleteEwJob();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [move, setMove] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const open = !!job && !ewJobClosed(job.status);
  const canWrite = canDispatch && open;
  const moves = canDispatch ? ewNextStatuses(job?.status) : [];
  // Quoted vs actual: a completed job, and a role that reads prices (the API's EW_READ).
  const showQva = !!job && job.status === 'completed' && canReadEwRates(user?.role);
  // A stale or shared ?tab=quoted-vs-actual on a job that cannot show it lands on Overview, not a blank body.
  // Office-only and completed-only tabs: anyone else asking for one lands on Overview.
  const tab: TabKey =
    (requestedTab === 'quoted-vs-actual' && !showQva) || (requestedTab === 'hours' && !canDispatch) ? 'overview' : requestedTab;
  const hb = holdbackLabel(job?.holdbackLabel ? { holdbackLabel: job.holdbackLabel } : meta);

  const doMove = () => {
    if (!job || !move) return;
    setMoveError(null);
    if (move === 'cancelled' && !cancelReason.trim()) return setMoveError('Say why the job is cancelled.');
    update.mutate(
      { id: job.id, body: move === 'cancelled' ? { status: move, cancel_reason: cancelReason.trim() } : { status: move } },
      {
        onSuccess: () => {
          toast.success(`Job ${ewJobStatusSkin(move).label.toLowerCase()}`);
          setMove(null);
          setCancelReason('');
        },
        onError: (err) => setMoveError(ewRefusal(err, 'The status could not be changed.')),
      },
    );
  };

  return (
    <div data-testid="ew-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.jobTypeLabel ? `${EW_EYEBROW} · ${job.jobTypeLabel}` : EW_EYEBROW}
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        description={job ? `${job.customerName ?? '—'} · ${job.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          job && canDispatch ? (
            <div className="flex flex-wrap gap-2" data-testid="ew-job-actions">
              {moves.map((to) => (
                <button
                  key={to}
                  type="button"
                  className={to === moves[0] && to !== 'cancelled' ? 'fi-action fi-action--standard fi-action--primary' : DH_BAND_SECONDARY}
                  onClick={() => { setMoveError(null); setCancelReason(''); setMove(to); }}
                >
                  {MOVE_LABEL[to] ?? ewJobStatusSkin(to).label}
                </button>
              ))}
              {open && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); pickTab('overview'); }} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {ewJobDeletable(job.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: job ? ewJobStatusSkin(job.status).label : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crew', tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.crew.length) : '—' },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Units', tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.equipment.length) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <EwPanel eyebrow="Job" title="Job">
              <EwEmpty>This job was not found. It may have been deleted, or you may not be on its crew.</EwEmpty>
            </EwPanel>
          ) : failure || !job ? (
            <EwPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </EwPanel>
          ) : (
            <>
              <EwJobWarningsBanner jobId={job.id} timeZone={meta?.timeZone ?? null} />
              <div role="tablist" aria-label="Job sections" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border">
                {TABS.filter((t) => (t.key !== 'quoted-vs-actual' || showQva) && (t.key !== 'hours' || canDispatch)).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => pickTab(t.key)}
                    className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                      tab === t.key ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-bold text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {t.label}
                    {t.key === 'crew' ? ` (${job.crew.length})` : t.key === 'equipment' ? ` (${job.equipment.length})` : ''}
                  </button>
                ))}
              </div>

              {tab === 'overview' && (
                <>
                  {!open && (
                    <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-job-closed">
                      This job is {ewJobStatusSkin(job.status).label.toLowerCase()}. It takes no more changes.
                      {job.cancelReason ? ` Reason: ${job.cancelReason}` : ''}
                    </p>
                  )}
                  <EwPanel eyebrow={job.jobTypeLabel ?? 'Job'} title="Details" testId="ew-job-details">
                    <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                      <EwSpec label="Status" value={<EwBadge skin={ewJobStatusSkin(job.status)} value={job.status} />} />
                      <EwSpec label="Customer" value={job.customerName || '—'} />
                      <EwSpec label="Site" value={job.siteName || '—'} />
                      <EwSpec label="Job type" value={job.jobTypeLabel || job.jobType || '—'} />
                      <EwSpec label="Contract" value={job.contractTypeLabel || job.contractType || '—'} />
                      <EwSpec label="Contract value" value={fmtMoney(job.contractValue, job.currency)} mono />
                      <EwSpec label={hb} value={jobHoldback(job)} />
                      <EwSpec label="Field tickets" value={catalogLabel(catalog?.ticketModes, job.ticketMode)} />
                      <EwSpec label="Supervisor" value={job.supervisorName || '—'} />
                      <EwSpec label="Scheduled" value={`${ewDay(job.scheduledStart)} → ${ewDay(job.scheduledEnd)}`} />
                      <EwSpec label="PO number" value={job.poNumber || '—'} mono />
                      <EwSpec label="AFE number" value={job.afeNumber || '—'} mono />
                      <EwSpec label="Cost code" value={job.costCode || '—'} mono />
                      <EwSpec label="Currency" value={job.currency || '—'} />
                    </dl>
                    {job.scope && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Scope: </span>{job.scope}</p>}
                    {job.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.notes}</p>}
                  </EwPanel>

                  {canAdminEw(user?.role) && <TruckBillingPanel job={job} />}

                  {(job.sourceQuote || (job.lines && job.lines.length > 0)) && (
                    <EwPanel eyebrow="Quote" title="Quoted lines" testId="ew-job-lines">
                      {job.sourceQuote && (
                        <p className="px-4 pt-3 text-sm text-fieldiq-text">
                          From quote{' '}
                          <button type="button" className="font-bold text-fi-amber underline" onClick={() => navigate(`/quotes/${job.sourceQuote!.id}`)}>
                            {job.sourceQuote.quoteNumber}
                          </button>
                        </p>
                      )}
                      {job.lines && job.lines.length > 0 ? (
                        <div className="overflow-x-auto px-4 py-3">
                          <table className="w-full text-sm">
                            <thead className="text-left text-xs text-fieldiq-muted">
                              <tr>
                                <th className="py-1 pr-3 font-bold">#</th>
                                <th className="py-1 pr-3 font-bold">Description</th>
                                <th className="py-1 pr-3 font-bold">Unit</th>
                                <th className="py-1 pr-3 text-right font-bold">Qty</th>
                                <th className="py-1 pr-3 text-right font-bold">Rate</th>
                                <th className="py-1 text-right font-bold">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {job.lines.map((l) => (
                                <tr key={l.id} className="border-t border-fieldiq-border text-fieldiq-text">
                                  <td className="py-1.5 pr-3">{l.lineNo}</td>
                                  <td className="py-1.5 pr-3">{l.description}</td>
                                  <td className="py-1.5 pr-3">{ewRateUnitWords(l.unit)}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono">{l.quantity}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono">{fmtMoney(Number(l.rate), job.currency)}</td>
                                  <td className="py-1.5 text-right font-mono">{fmtMoney(Number(l.amount), job.currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="px-4 py-3 text-sm text-fieldiq-muted">No lines.</p>
                      )}
                    </EwPanel>
                  )}

                  {editing && canWrite && (
                    <EwPanel eyebrow="Edit" title="Edit job" testId="ew-job-edit">
                      <EwJobForm
                        job={job}
                        catalog={catalog}
                        meta={meta}
                        customers={customers.data ?? []}
                        users={users.data ?? []}
                        pending={update.isPending}
                        error={editError}
                        submitLabel="Save job"
                        onCancel={() => setEditing(false)}
                        onSubmit={(body) =>
                          update.mutate(
                            { id: job.id, body },
                            {
                              onSuccess: () => {
                                toast.success('Job saved');
                                setEditing(false);
                              },
                              onError: (err) => setEditError(ewRefusal(err, 'The job could not be saved.')),
                            },
                          )
                        }
                      />
                    </EwPanel>
                  )}
                </>
              )}

              {tab === 'crew' && <CrewSection job={job} catalog={catalog} users={users.data ?? []} canWrite={canWrite} showHours={canDispatch} />}
              {tab === 'equipment' && <EquipmentSection job={job} equipment={equipment.data ?? []} canWrite={canWrite} />}
              {tab === 'logs' && <DailyLogsSection jobId={job.id} onOpen={openRecord} canWrite={canWrite} />}
              {tab === 'rent-ins' && <RentInsSection jobId={job.id} catalog={catalog} currency={job.currency ?? meta?.currency ?? null} canWrite={canWrite} />}
              {tab === 'permits' && <PermitsSection jobId={job.id} catalog={catalog} canWrite={canWrite} />}
              {tab === 'hazmat' && <HazmatSection jobId={job.id} catalog={catalog} canWrite={canWrite} />}
              {tab === 'tickets' && <JobTicketsSection job={job} today={meta?.today ?? null} canWrite={canDispatch} onOpen={openRecord} />}
              {tab === 'change-orders' && <JobChangeOrdersSection job={job} canWrite={canDispatch} onOpen={openRecord} />}
              {tab === 'hours' && canDispatch && <EwJobEmployeeHoursSection jobId={job.id} />}
              {tab === 'invoices' && (
                <EwJobInvoicesSection job={{ id: job.id, status: job.status, currency: job.currency ?? meta?.currency ?? null }} onOpen={openRecord} />
              )}
              {tab === 'quoted-vs-actual' && showQva && <EwQvaJobTab jobId={job.id} />}
            </>
          )}
        </div>
      </section>

      {job && canDispatch && (
        <>
          <EwDialog
            open={!!move}
            title={move ? MOVE_LABEL[move] ?? 'Change status' : 'Change status'}
            onClose={() => setMove(null)}
            footer={
              <>
                <button type="button" className={EW_SECONDARY} onClick={() => setMove(null)} disabled={update.isPending}>
                  Not now
                </button>
                <button type="button" className={EW_PRIMARY} onClick={doMove} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : move ? MOVE_LABEL[move] ?? 'Confirm' : 'Confirm'}
                </button>
              </>
            }
          >
            <EwFormError message={moveError} />
            <p className="text-sm text-fieldiq-text">{move ? MOVE_BODY[move] : ''}</p>
            {move === 'cancelled' && (
              <div className="mt-3">
                <EwField label="Reason">
                  <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Cancel reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </EwField>
              </div>
            )}
          </EwDialog>
          <EwDialog
            open={deleting}
            title="Delete job"
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                  Keep job
                </button>
                <button
                  type="button"
                  className={EW_PRIMARY}
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(job.id, {
                      onSuccess: () => {
                        toast.success('Job deleted');
                        navigate('/earthworks/jobs', { replace: true });
                      },
                      onError: (err) => setDeleteError(ewRefusal(err, 'The job could not be deleted.')),
                    })
                  }
                >
                  {del.isPending ? 'Deleting…' : 'Delete job'}
                </button>
              </>
            }
          >
            <EwFormError message={deleteError} />
            <p className="text-sm text-fieldiq-text">Removes {job.jobNumber} from the register. Only a draft or scheduled job can be deleted — cancel any other.</p>
          </EwDialog>
        </>
      )}
    </div>
  );
}
